<script setup lang="ts">
import { VDateInput } from 'vuetify/labs/VDateInput'
import type MDBDegréJourSource from '@/types/degréjour/degrejoursource'
import type MDBDegréJour from '@/types/degréjour/degrejour'
import type MDBDegréJourHistorique from '@/types/degréjour/degrejourhistorique'
import type { ECOption } from '@/plugins/echarts'
import { roundTo, notifyError } from '@/utils/helpers'



const storeU = useUtilisateurStore()
const sources = ref([] as MDBDegréJourSource[])
const sourcesNom = computed(() => sources.value.map(s => s.nom))
const sourceSélectionnée = ref(null as MDBDegréJourSource | null | undefined)
const sourceSélectionnéeNom = computed(() => sourceSélectionnée.value?.nom ?? 'Aucune')
const révision = ref('')

declare interface DegréJourData {
  date: string
  degréJour: number | undefined
  degréJourFutur: number | undefined
  degréJourSemestriel: number | undefined
}
const degréJoursData = ref([] as DegréJourData[])
watch(degréJoursData, (newData) => {
  chartOptions.value.dataset = { source: newData, dimensions: ['date', 'degréJour', 'degréJourFutur', 'degréJourSemestriel'] }
}, { deep: true })

watch(() => storeU.annéeSélectionnée, async (_) => {
  await getDegréJours()
  await getDegréJoursHistorique()
})
watch(() => sourceSélectionnée.value, async (_) => { await getDegréJours() })

// Revision check
const intervalId = setInterval(async () => {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (!sourceSélectionnée.value || siteCS == undefined || siteCS == '') {
    return
  }

  const result = await apiProphet.get(`sites/${siteCS}/revisions/degrejours`)
  if (révision.value != result.data) {
    getDegréJours()
  }
}, 3000)

const onSourceChange = async (source: string) => {
  sourceSélectionnée.value = sources.value.find(s => s.nom === source)
}

async function getDegréJours() {
  degréJoursData.value = []

  if (!sourceSélectionnée.value) {
    Notify.info('Aucune source de degré-jours sélectionnée')
    return
  }

  try {
      const find = sourceSélectionnée.value
      const siteCS = storeU.siteSélectionné.lettresDappel

      const revision = await apiProphet.get(`sites/${siteCS}/revisions/degrejours`)
      révision.value = revision.data

      for (let i = 1; i < 12; ++i) {
        const config = { params: { année: storeU.annéeSélectionnée, mois: i } }
        const result = await apiProphet.get(`sites/${siteCS}/degrejours/${find.id}/actuel/mm`, config)
        const filtered = result.data.filter((d: MDBDegréJour) => new Date(d.date).getMonth() > 2)
        degréJoursData.value.push(...(filtered.map(
          (d: MDBDegréJour) => { return { date: d.date, degréJour: roundTo(d.degréJour, 2) }})
        ))
      }
      if (storeU.annéeSélectionnée >= new Date().getFullYear()) {
        let foundOne = false
        for (let i = 1; i < 12; ++i) {
          const config = { params: { année: storeU.annéeSélectionnée, mois: i } }
          const result = await apiProphet.get(`sites/${siteCS}/degrejours/${find.id}/futur/mm`, config)

          // Set d.degréJourFutur of date that already exists, or add new if it doesn't exist
          result.data.forEach((d: MDBDegréJour) => {
            const index = degréJoursData.value.findIndex((dd: DegréJourData) => dd.date === d.date)
            if (index >= 0) { degréJoursData.value[index].degréJourFutur = roundTo(d.degréJour, 2) }
            else {
              if (d.degréJour > 0)
                foundOne = true
              if (foundOne)
                degréJoursData.value.push({
                  date: d.date, degréJour: undefined,
                  degréJourFutur: roundTo(d.degréJour, 2), degréJourSemestriel: undefined
                })
            }
          })
        }

        // Prévision 10 jours
        if (storeU.annéeSélectionnée == new Date().getFullYear()) {
          const result = await apiProphet.get(`sites/${siteCS}/degrejours/${find.id}/semestriel`)
          result.data.forEach((d: MDBDegréJour) => {
            const index = degréJoursData.value.findIndex((dd: DegréJourData) => dd.date === d.date)
            if (index >= 0) { degréJoursData.value[index].degréJourSemestriel = d.degréJour }
            else { degréJoursData.value.push({
              date: d.date, degréJour: undefined, degréJourFutur: undefined,
              degréJourSemestriel: roundTo(d.degréJour, 2)
            }) }
          })
        }
      }
      //degréJours.value = degréJours.value.sort((a, b) => a.date.localeCompare(b.date))
      degréJoursData.value = degréJoursData.value.sort((a, b) => a.date.localeCompare(b.date))
    }
    catch (error) {
      console.error(error)
      Notify.error(error)
    }
}

//https://echarts.apache.org/en/option.html#tooltip
const chartOptions = ref<ECOption>({
  backgroundColor: 'transparent',
  visualMap: { show: false, type: 'continuous', min: 0, max: 25 },
  tooltip: { trigger: 'axis' },
  grid: { top: 20, left: '2%', right: '2%', bottom: '3%', containLabel: true },
  xAxis: {
    type: 'category',
    axisLabel: {
      rotate: 45, // Rotate the labels by 45 degrees
      formatter: function (value) {
        const date = new Date(value);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${day}/${month}`;
      },
    }
  },
  yAxis: { type: 'value' },
  series: [
    { name: 'DJ Réels', type: 'line', showSymbol: false, lineStyle: { width: 2 } },
    { name: 'DJ Moy. 5 ans', type: 'line', showSymbol: false, lineStyle: { width: 2 } },
    { name: 'DJ Prévision 10 jours', type: 'line', showSymbol: false, lineStyle: { width: 2 } }
  ]
})

const simDate = ref(new Date(storeU.annéeSélectionnée, new Date().getMonth(), new Date().getDate()))
const simNombreDJ = ref(0)
const simInverse = ref(false)
const simRésultat = computed(() => {
  let startDate = new Date(simDate.value)
  if (startDate.getFullYear() != storeU.annéeSélectionnée) {
    startDate = new Date(storeU.annéeSélectionnée, startDate.getMonth(), startDate.getDate())
    return startDate
  }

  let djI = degréJoursData.value.findIndex(d => new Date(d.date) > startDate)

  let date = '';
  let djNeed = simNombreDJ.value
  while (djNeed > 0) {

    if (simInverse.value) {
      if (--djI < 0) {
        break
      }
    } else if (++djI >= degréJoursData.value.length) {
      break
    }

    const dj = degréJoursData.value[djI]
    date = dj.date
    if (dj.degréJour && dj.degréJour > 0) {
      djNeed -= dj.degréJour
    }
    else if (dj.degréJourSemestriel) {
      djNeed -= dj.degréJourSemestriel
    }
    else if (dj.degréJourFutur && dj.degréJourFutur > 0) {
      djNeed -= dj.degréJourFutur
    }
  }

  return date
})

declare interface DegréJourHistoriqueData {
  date: string
  degréJourSemé: number | undefined
  nombreSemé: number
  degréJourPlanté: number | undefined
  nombrePlanté: number
  degréJourPlantTape: number | undefined
  nombrePlantTape: number
}
const degréJoursHistoriqueData = ref([] as DegréJourHistoriqueData[])
watch(degréJoursHistoriqueData, (newData) => {
  chartDJHistoriqueOptions.value.dataset = { source: newData, dimensions: ['date', 'degréJourSemé', 'degréJourPlanté', 'degréJourPlantTape'] }
}, { deep: true })

const chartDJHistoriqueOptions = ref<ECOption>({
  backgroundColor: 'transparent',
  visualMap: { show: false, type: 'continuous', min: 0, max: 1100 },
  tooltip: { trigger: 'axis' },
  grid: { top: 20, left: '2%', right: '2%', bottom: '3%', containLabel: true },
  xAxis: { type: 'category',
    axisLabel: {
      rotate: 45, // Rotate the labels by 45 degrees
      formatter: function (value) {
        const date = new Date(value);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${day}/${month}`;
      },
    }
  },
  yAxis: { type: 'value' },
  series: [
    { name: 'DJ Semé', type: 'line', showSymbol: false, lineStyle: { width: 2 } },
    { name: 'DJ Planté', type: 'line', showSymbol: false, lineStyle: { width: 2 } },
    { name: 'DJ PlantTape', type: 'line', showSymbol: false, lineStyle: { width: 2 } }
  ]
})

const dialogCulture = useTemplateRef('dialogCulture')
async function showDialogCulture() {
  const result = await dialogCulture.value?.openVariétéFew(['Iceberg / Pommée', 'Laitue Romaine',
    'Radis Chinois', 'Carotte', 'Oignon Jaune', 'Oignon Rouge', 'Patate Douce'])

  if (result != undefined && result != '') {
    const split = result.split(':')
    const culture = split[0]
    const variété = split[1]
    storeU.sélectionnerCulture(culture, variété)
    await getDegréJoursHistorique()
  }
}

async function getDegréJoursHistorique() {
  const year = storeU.annéeSélectionnée
  const siteCS = storeU.siteSélectionné.lettresDappel
  const config = { params: { culture: storeU.cultureSélectionnée.nom, variete: storeU.variétéSélectionnée.nom, anneeMin: year - 6, anneeMax: year - 1 } }
  const result = await apiProphet.get(`sites/${siteCS}/degrejours/historique`, config)
  degréJoursHistoriqueData.value = []

  result.data.sort((a: MDBDegréJourHistorique, b: MDBDegréJourHistorique) => a.semaine - b.semaine)
  result.data.forEach((d: MDBDegréJourHistorique) => {
    const simple = new Date(year, 0, 1 + (d.semaine - 1) * 7)
    const dateOnly = simple.toISOString().split('T')[0]
    const index = degréJoursHistoriqueData.value.findIndex((dd: DegréJourHistoriqueData) => dd.date === dateOnly)
    let foundData = null as DegréJourHistoriqueData | null

    if (index >= 0) {
      foundData = degréJoursHistoriqueData.value[index]
    }
    else {
      foundData = {
        date: dateOnly,
        degréJourSemé: undefined,
        nombreSemé: 0,
        degréJourPlanté: undefined,
        nombrePlanté: 0,
        degréJourPlantTape: undefined,
        nombrePlantTape: 0
      }

      degréJoursHistoriqueData.value.push(foundData)
    }

    if (d.typePlantation == 'Semée') {
      if (foundData.degréJourSemé == undefined) {
        foundData.degréJourSemé = 0
      }
      foundData.degréJourSemé += d.degréJour
      foundData.nombreSemé += 1
    } else if (d.typePlantation == 'Plantée') {
      if (foundData.degréJourPlanté == undefined) {
        foundData.degréJourPlanté = 0
      }
      foundData.degréJourPlanté += d.degréJour
      foundData.nombrePlanté += 1
    } else if (d.typePlantation == 'PlantTape') {
      if (foundData.degréJourPlantTape == undefined) {
        foundData.degréJourPlantTape = 0
      }
      foundData.degréJourPlantTape += d.degréJour
      foundData.nombrePlantTape += 1
    } else {
      if (foundData.degréJourSemé == undefined) {
        foundData.degréJourSemé = 0
      }
      foundData.degréJourSemé += d.degréJour
      foundData.nombreSemé += 1
    }
  })

  degréJoursHistoriqueData.value.forEach(element => {
    if (element.degréJourSemé != undefined) {
      element.degréJourSemé = roundTo(element.degréJourSemé / element.nombreSemé, 2)
    }
    if (element.degréJourPlanté != undefined) {
      element.degréJourPlanté = roundTo(element.degréJourPlanté / element.nombrePlanté, 2)
    }
    if (element.degréJourPlantTape != undefined) {
      element.degréJourPlantTape = roundTo(element.degréJourPlantTape / element.nombrePlantTape, 2)
    }
  });
}

onMounted(async () => {
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    const result = await apiProphet.get(`sites/${siteCS}/degrejours/sources`)
    sources.value = result.data
  }
  catch (error) {
    notifyError(error)
  }

  if (sources.value.length > 0) {
    sourceSélectionnée.value = sources.value[0]
  }

  if (storeU.cultureSélectionnée.nom != '' && storeU.variétéSélectionnée.nom != '') {
    await getDegréJoursHistorique()
  }
})

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-container fluid>
    <v-card class="d-flex flex-column">
      <v-toolbar>
        <v-toolbar-title class="text-h5">
          Degré-jours
          <YearSelector/>
        </v-toolbar-title>
        <template #append>
          <v-select v-model="sourceSélectionnéeNom" class="pt-4" width="500px" :items="sourcesNom" label="Source" outlined dense @update:model-value="onSourceChange"/>
        </template>
      </v-toolbar>
      <v-chart style="min-width: 200px; min-height: 400px;" :option="chartOptions" autoresize />
    </v-card>

    <v-card class="d-flex flex-column mt-4">
      <v-card-title class="text-h5">Simulation de degré-jours</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-date-input v-model="simDate" :label="simInverse ? 'Date récolte' : 'Date semé'" display-format="fullDateWithWeekday"/>
            <v-date-input v-model="simRésultat" :label="simInverse ? 'Date semé prévue' : 'Date prévue'" readonly density="compact" variant="outlined" display-format="fullDateWithWeekday"/>
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="simNombreDJ" label="Nombre de degré-jours" type="number" required/>
            <v-switch v-model="simInverse" inset class="ml-2 mr-1" label="Simulation inverse"/>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="d-flex flex-column mt-4">
      <v-toolbar>
        <v-toolbar-title class="text-h5">
          Degré-jours pour une culture
        </v-toolbar-title>
        <template #append>
          <span class="text-h6 ps-2">
            {{ storeU.cultureSélectionnée.nom }} : {{ storeU.variétéSélectionnée.nom }}
            <v-btn color="primary" class="mr-2 ml-2" size="40px" elevation="4" icon="M21,9L17,5V8H10V10H17V13M7,11L3,15L7,19V16H14V14H7V11Z" @click="showDialogCulture"/>
          </span>
        </template>
      </v-toolbar>
      <v-chart style="min-width: 200px; min-height: 400px;" :option="chartDJHistoriqueOptions" autoresize />
    </v-card>
  </v-container>

  <DialogCulture ref='dialogCulture'/>
</template>
